/**
 * WAF managed-DNS zone onboarding ("Add site") — Owl mDNS zone lifecycle:
 * eligibility pre-check → create → scan → review → publish → delegate (nameservers
 * + NS-status). Service-scoped (path `/services/:service_id/waf/…`), zone-anchored (`?zone_id=`).
 *
 * Backed by `modules/service/waf/controller_dns_write.go` + `controller_dns.go`:
 *   - POST `/services/:service_id/waf/dns/zones/eligibility` → can this domain be onboarded
 *   - POST `/services/:service_id/waf/dns/zones`             → create the zone (zone_status 0 = unpublished)
 *   - POST `/services/:service_id/waf/dns/zones/scan`        → scan current authoritative DNS for import candidates
 *   - GET  `/services/:service_id/waf/dns/zones/review`      → staged records before publish (paginated)
 *   - POST `/services/:service_id/waf/dns/zones/publish`     → activate the zone with a dns_provider mode
 *   - GET  `/services/:service_id/waf/dns/nameservers`       → managed nameservers the customer delegates to
 *   - GET/PUT `/services/:service_id/waf/dns/ns-status`      → delegation check (PUT forces a re-check)
 *
 * Per-record create reuses `useCreateWafDnsRecord` (`./dns`). Returned records use
 * the shared `WafDnsRecord` shape.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  type PageInfo,
} from './_shared';
import type { WafDnsRecord } from './dns';

/** Add-site eligibility verdict — `action` is the onboarding action, `root_domain` the registrable root. */
export interface WafZoneEligibility {
  action: string;
  root_domain: string;
}

/** A managed-DNS zone resource (create/publish response). `zone_status` 0 = unpublished, active otherwise. */
export interface WafDnsZoneDetail {
  id: string;
  org_id: string;
  domain_name: string;
  zone_status: number;
  ns_status: number;
  dns_provider: number;
  created_at: string;
}

/** Staged-zone review payload before publish. */
export interface WafZoneReview {
  zone_id: string;
  zone_name: string;
  zone_status: number;
  ns_status: number;
  dns_provider: number;
  records: WafDnsRecord[];
  pagination?: PageInfo;
}

/** One managed nameserver host the customer delegates to. */
export interface WafNameserver {
  host: string;
  region: string;
}

/** Delegation check: `matched` is canonical; `status` keeps the raw vendor verdict. */
export interface WafNsStatus {
  zone_id: string;
  matched: boolean;
  status: string;
}

export interface WafZoneReviewFilter {
  page?: number;
  pageSize?: number;
  /** Narrow to CDN-eligible records; defaults to "cdn_domain". */
  target?: string;
}

/** Pre-check whether a domain can be onboarded as a site (`POST /waf/dns/zones/eligibility`). */
export function useCheckZoneEligibility(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafZoneEligibility, Error, { domain_name: string }>,
) {
  return useMutation({
    mutationFn: async ({ domain_name }: { domain_name: string }) =>
      unwrap<WafZoneEligibility>(
        await serviceApiV3.post(`/services/${serviceId}/waf/dns/zones/eligibility`, { domain_name }),
      ),
    ...options,
  });
}

/** Onboard a domain as a managed-DNS zone (`POST /waf/dns/zones`). */
export function useCreateDnsZone(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafDnsZoneDetail, Error, { domain_name: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain_name }: { domain_name: string }) =>
      unwrap<WafDnsZoneDetail>(
        await serviceApiV3.post(`/services/${serviceId}/waf/dns/zones`, { domain_name }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-zones'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Scan the domain's current DNS for import candidates (`POST /waf/dns/zones/scan`). */
export function useScanZoneRecords(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafDnsRecord[], Error, { zoneId: string }>,
) {
  return useMutation({
    mutationFn: async ({ zoneId }: { zoneId: string }) =>
      unwrap<WafDnsRecord[]>(
        await serviceApiV3.post(`/services/${serviceId}/waf/dns/zones/scan`, null, {
          params: { zone_id: zoneId },
        }),
      ),
    ...options,
  });
}

/** Delete a managed-DNS zone ("site") (`DELETE /waf/dns/zones/{id}`); cascade removes its records/config. */
export function useDeleteDnsZone(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { zoneId: string; cascade?: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, cascade = true }: { zoneId: string; cascade?: boolean }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/dns/zones/${zoneId}`, {
        params: { cascade: cascade ? 'true' : 'false' },
      });
    },
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-zones'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Review a zone's staged records before publish (`GET /waf/dns/zones/review`, paginated). */
export function useZoneReview(
  serviceId: string | undefined,
  zoneId: string | undefined,
  filter: WafZoneReviewFilter = {},
  enabled = true,
) {
  const { page = 1, pageSize = 50, target = 'cdn_domain' } = filter;
  const params = { zone_id: zoneId, page, page_size: pageSize, target };
  return useQuery<WafZoneReview>({
    queryKey: wafKeys.dnsZoneReview(serviceId ?? '', zoneId ?? '', { page, page_size: pageSize, target }),
    enabled: enabled && !!serviceId && !!zoneId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<WafZoneReview>(await serviceApiV3.get(`/services/${serviceId}/waf/dns/zones/review`, { params })),
  });
}

/** Activate a staged zone with the chosen DNS-provider mode (`POST /waf/dns/zones/publish`). */
export function usePublishZone(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafDnsZoneDetail, Error, { zoneId: string; dnsProvider: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId, dnsProvider }: { zoneId: string; dnsProvider: number }) =>
      unwrap<WafDnsZoneDetail>(
        await serviceApiV3.post(`/services/${serviceId}/waf/dns/zones/publish`, { dns_provider: dnsProvider }, {
          params: { zone_id: zoneId },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'dns-zones'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Managed nameservers the customer delegates to (`GET /waf/dns/nameservers`). */
export function useWafNameservers(serviceId: string | undefined) {
  return useQuery<WafNameserver[]>({
    queryKey: wafKeys.dnsNameservers(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 60 * 60_000,
    queryFn: async () =>
      unwrap<{ nameservers?: WafNameserver[] }>(
        await serviceApiV3.get(`/services/${serviceId}/waf/dns/nameservers`),
      )?.nameservers ?? [],
  });
}

/** A zone's NS-delegation status (`GET /waf/dns/ns-status`). */
export function useNsStatus(
  serviceId: string | undefined,
  zoneId: string | undefined,
  enabled = true,
) {
  return useQuery<WafNsStatus>({
    queryKey: wafKeys.dnsNsStatus(serviceId ?? '', zoneId ?? ''),
    enabled: enabled && !!serviceId && !!zoneId,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<WafNsStatus>(
        await serviceApiV3.get(`/services/${serviceId}/waf/dns/ns-status`, {
          params: { zone_id: zoneId },
        }),
      ),
  });
}

/** Force a fresh NS-delegation check (`PUT /waf/dns/ns-status`). */
export function useRefreshNsStatus(
  serviceId: string | undefined,
  options?: UseMutationOptions<WafNsStatus, Error, { zoneId: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ zoneId }: { zoneId: string }) =>
      unwrap<WafNsStatus>(
        await serviceApiV3.put(`/services/${serviceId}/waf/dns/ns-status`, null, {
          params: { zone_id: zoneId },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      const [data, vars] = args;
      if (serviceId) qc.setQueryData(wafKeys.dnsNsStatus(serviceId, vars.zoneId), data);
      options?.onSuccess?.(...args);
    },
  });
}
